<script setup>
import { defineProps, defineEmits } from 'vue';

const props = defineProps({
  modelValue: {
    type: Number,
    required: true,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  step: {
    type: Number,
    default: 0.1,
  },
  label: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showMinMax: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'input', 'change']);

const handleInput = event => {
  const value = event.target.valueAsNumber || parseFloat(event.target.value);
  emit('update:modelValue', value);
  emit('input', value);
};

const handleChange = event => {
  const value = event.target.valueAsNumber || parseFloat(event.target.value);
  emit('change', value);
};
</script>

<template>
  <div class="w-full">
    <div v-if="label" class="flex justify-between items-center mb-2">
      <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
        {{ label }}
      </label>
      <span class="text-sm text-slate-500 dark:text-slate-400">
        {{ modelValue }}
      </span>
    </div>
    <input
      :id="id"
      :value="modelValue"
      type="range"
      :min="min"
      :max="max"
      :step="step"
      :disabled="disabled"
      class="w-full h-1.5 bg-slate-75 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer transition-opacity duration-200 opacity-70 hover:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed slider-thumb"
      @input="handleInput"
      @change="handleChange"
    />
    <div
      v-if="showMinMax"
      class="flex justify-between text-xs text-slate-500 dark:text-slate-400 mt-1"
    >
      <span>{{ min }}</span>
      <span>{{ max }}</span>
    </div>
  </div>
</template>

<style scoped>
/* Custom thumb styles that can't be achieved with Tailwind alone */
.slider-thumb::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #645298;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s;
}

.slider-thumb::-webkit-slider-thumb:hover {
  background: #645298;
  transform: scale(1.1);
}

.slider-thumb:disabled::-webkit-slider-thumb {
  background: #dddde3;
  cursor: not-allowed;
  transform: none;
}

.slider-thumb::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #645298;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s;
}

.slider-thumb::-moz-range-thumb:hover {
  background: #645298;
  transform: scale(1.1);
}

.slider-thumb:disabled::-moz-range-thumb {
  background: #dddde3;
  cursor: not-allowed;
  transform: none;
}
</style>
